import { get } from 'lodash';
import DatadogHandler from 'utils/datadog';
import { actionHandlerWithFeatureFlag } from 'utils';
import { FEATURE_FLAGS } from 'utils/constants';
import {
  msCancelConsentForm,
  msCompleteConsentForm,
  msGetAllOfflineConsentFormAction,
  msGetFormSigner,
  msGetRegistrationConsentUploadURL,
  msSendConsentForm,
} from 'services/RegistrationsService';
import {
  FETCH_REG_CONSENT_FORM_SIGNER,
  GET_ALL_OFFLINE_CONSENT_FORM_ACTION,
} from '../actionTypes';

import {
  GET_REG_CONSENT_FORM_SIGNER,
  CANCEL_CONSENT_FORM,
  SEND_CONSENT_FORM,
  CONSENT_FORM_COMPLETED,
  SAVE_REG_CONSENT_OFFLINE,
  GET_REG_CONSENT_UPLOAD_URL,
  GET_ALL_OFFLINE_CONSENT_FORM,
} from '../../models/consentModelsV2';
import { SkApolloRequest } from '../../../utils/apolloUtils';

const fileName = 'src/redux/actions/Registration/ConsentV2.js';

const dispatchConsentFormList = (
  dispatch,
  inProgress = true,
  data = {},
  error = null
) => {
  dispatch({
    type: FETCH_REG_CONSENT_FORM_SIGNER,
    key: 'regConsentFormSigners',
    value: {
      inProgress,
      data,
      error,
    },
  });
};

const dispatchgetAllOfflineConsentForm = (
  dispatch,
  inProgress = true,
  data = {},
  error = null
) => {
  dispatch({
    type: GET_ALL_OFFLINE_CONSENT_FORM_ACTION,
    key: 'regAllOfflineConsentForm',
    value: {
      inProgress,
      data,
      error,
    },
  });
};

export const monoGetRegConsentSignerList = async (_, reqData) => {
  try {
    const IDRegistration = get(reqData, 'IDRegistration');
    const response = await SkApolloRequest({
      params: {
        query: GET_REG_CONSENT_FORM_SIGNER,
        variables: { IDRegistration },
      },
      type: 'query',
    });

    return response;
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'monoGetRegConsentSignerList',
        fileName,
      },
      'error'
    );
    const errorMessage = ex.message.replace(/GraphQL error./, '');
    return { error: errorMessage };
  }
};

export const getRegConsentSignerList = IDRegistration => async dispatch => {
  dispatchConsentFormList(dispatch);
  const resp = await actionHandlerWithFeatureFlag(
    FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
    {
      dispatch,
      msHandler: msGetFormSigner,
      monoHandler: monoGetRegConsentSignerList,
      extraData: { IDRegistration },
    }
  );

  if (resp.success) {
    dispatchConsentFormList(dispatch, false, resp.data);
  } else {
    dispatchConsentFormList(dispatch, false, null, resp.error);
  }

  return resp;
};

export const monoSendRegConsentForm = async (_, reqData) => {
  try {
    const IDRegistration = get(reqData, 'IDRegistration');
    const participants = get(reqData, 'participants');

    const response = await SkApolloRequest({
      params: {
        mutation: SEND_CONSENT_FORM,
        variables: {
          registrationId: IDRegistration,
          formParticipants: participants,
        },
      },
      type: 'mutation',
    });

    return response;
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'monoSendRegConsentForm',
        fileName,
      },
      'error'
    );
    const errorMessage = ex.message.replace(/GraphQL error./, '');
    return { error: errorMessage };
  }
};

export const sendRegConsentForm =
  (IDRegistration, participants) => async () => {
    return actionHandlerWithFeatureFlag(
      FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
      {
        msHandler: msSendConsentForm,
        monoHandler: monoSendRegConsentForm,
        extraData: { IDRegistration, participants },
      }
    );
  };

export const monoCancelConstentForm = async (_, reqData) => {
  try {
    const IDRegistration = get(reqData, 'IDRegistration');
    const response = await SkApolloRequest({
      params: {
        mutation: CANCEL_CONSENT_FORM,
        variables: {
          registrationId: IDRegistration,
        },
      },
      type: 'mutation',
    });

    return response;
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'monoCancelConstentForm',
        fileName,
      },
      'error'
    );
    const errorMessage = ex.message.replace(/GraphQL error./, '');
    return { error: errorMessage };
  }
};

export const cancelConsentForm = IDRegistration => async () => {
  return actionHandlerWithFeatureFlag(
    FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
    {
      msHandler: msCancelConsentForm,
      monoHandler: monoCancelConstentForm,
      extraData: { IDRegistration },
    }
  );
};

export const monoCompleteConsentForm = async (_, reqData) => {
  try {
    const IDRegistration = get(reqData, 'IDRegistration');
    const response = await SkApolloRequest({
      params: {
        mutation: CONSENT_FORM_COMPLETED,
        variables: {
          registrationId: IDRegistration,
        },
      },
      type: 'mutation',
    });

    return response;
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'monoCompleteConsentForm',
        fileName,
      },
      'error'
    );
    const errorMessage = ex.message.replace(/GraphQL error./, '');
    return { error: errorMessage };
  }
};

export const completeConsentForm = IDRegistration => async () => {
  return actionHandlerWithFeatureFlag(
    FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
    {
      msHandler: msCompleteConsentForm,
      monoHandler: monoCompleteConsentForm,
      extraData: { IDRegistration },
    }
  );
};

export const saveRegConsentOffline =
  (registrationId, fileURL, fileName) => async () => {
    try {
      const response = await SkApolloRequest({
        params: {
          mutation: SAVE_REG_CONSENT_OFFLINE,
          variables: {
            registrationId,
            fileURL,
            fileName,
          },
        },
        type: 'mutation',
      });

      return response;
    } catch (ex) {
      const errorMessage = ex.message.replace(/GraphQL error./, '');
      return { error: errorMessage };
    }
  };

export const monoGetRegistrationConsentUploadURL = async (_, reqData) => {
  try {
    const registrationID = get(reqData, 'registrationID');
    const file = get(reqData, 'file');

    return SkApolloRequest({
      params: {
        mutation: GET_REG_CONSENT_UPLOAD_URL,
        variables: {
          registrationID,
          file,
        },
      },
      type: 'mutation',
    });
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'monoGetRegistrationConssentUploadURL',
        fileName,
      },
      'error'
    );
    const errorMessage = ex.message.replace(/GraphQL error./, '');
    return { error: errorMessage };
  }
};

export const getRegistrationConsentUploadURL =
  (registrationID, file) => async () => {
    return actionHandlerWithFeatureFlag(
      FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
      {
        msHandler: msGetRegistrationConsentUploadURL,
        monoHandler: monoGetRegistrationConsentUploadURL,
        extraData: { registrationID, file },
      }
    );
  };

export const monoGetAllOfflineConsentFormAction = async (_, reqData) => {
  try {
    const registrationId = get(reqData, 'registrationId');
    const response = await SkApolloRequest({
      params: {
        query: GET_ALL_OFFLINE_CONSENT_FORM,
        variables: { registrationId },
      },
      type: 'query',
    });

    return response;
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'monoGetAllOfflineConsentFormAction',
        fileName,
      },
      'error'
    );
    const errorMessage = ex.message.replace(/GraphQL error./, '');
    return { error: errorMessage };
  }
};

export const getAllOfflineConsentFormAction =
  registrationId => async dispatch => {
    dispatchgetAllOfflineConsentForm(dispatch);

    const resp = await actionHandlerWithFeatureFlag(
      FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
      {
        dispatch,
        msHandler: msGetAllOfflineConsentFormAction,
        monoHandler: monoGetAllOfflineConsentFormAction,
        extraData: { registrationId },
      }
    );

    if (resp.success) {
      dispatchgetAllOfflineConsentForm(dispatch, false, resp.data);
    } else {
      dispatchgetAllOfflineConsentForm(dispatch, false, null, resp.error);
    }

    return resp;
  };
